import type { AxiosError } from 'axios';

import {
  BadGatewayError,
  BadRequestError,
  CanceledError,
  ConflictError,
  ConnectionTimeoutError,
  ContentTooLargeError,
  ForbiddenError,
  InternalServerError,
  NetworkUnavailableError,
  NotAcceptableError,
  NotAllowedError,
  NotFoundError,
  NotImplementedError,
  PaymentRequiredError,
  PreconditionFailedError,
  RecentAuthenticationRequiredError,
  ServiceUnavailableError,
  UnauthorizedError,
  UnprocessableContentError,
  UnsupportedMediaTypeError,
} from '@/api/errors.ts';

interface ErrorDetailObject {
  code?: string;
  message?: string;
}

interface ErrorData {
  detail: string | ErrorDetailObject | null;
}

export const handleApiError = (error: AxiosError) => {
  const errorMessage = error.message ? error.message.toLowerCase() : null;

  if (error.code === 'ECONNABORTED' || (errorMessage && errorMessage.includes('connection'))) {
    throw new ConnectionTimeoutError('Connection timeout');
  } else if (error.code === 'ENETWORK' || (errorMessage && errorMessage.includes('network'))) {
    // Network unavailable
    throw new NetworkUnavailableError('Network unavailable');
  } else if (errorMessage && errorMessage === 'canceled') {
    // Canceled request , don't throw error;
    throw new CanceledError('Canceled request');
  }

  if (!error.response) {
    throw new Error('An error occurred: ' + error.message);
  }

  const errorData: ErrorData = error.response.data as ErrorData;
  const detail = errorData.detail;
  const errorDataMessage = typeof detail === 'string' ? detail : detail?.message || error.message;
  const errorCode = typeof detail === 'object' && detail !== null ? detail.code : undefined;

  switch (error.response.status) {
    case 400:
      throw new BadRequestError(errorDataMessage);
    case 401:
      throw new UnauthorizedError(errorDataMessage);
    case 402:
      throw new PaymentRequiredError(errorDataMessage);
    case 403:
      if (errorCode === 'recent_authentication_required') {
        throw new RecentAuthenticationRequiredError(errorDataMessage);
      }
      throw new ForbiddenError(errorDataMessage, errorCode);
    case 404:
      throw new NotFoundError(errorDataMessage);
    case 405:
      throw new NotAllowedError(errorDataMessage);
    case 406:
      throw new NotAcceptableError(errorDataMessage);
    case 409:
      throw new ConflictError(errorDataMessage, errorCode);
    case 412:
      throw new PreconditionFailedError(errorDataMessage);
    case 413:
      throw new ContentTooLargeError(errorDataMessage);
    case 415:
      throw new UnsupportedMediaTypeError(errorDataMessage);
    case 422:
      throw new UnprocessableContentError(errorDataMessage);
    case 500:
      throw new InternalServerError(errorDataMessage);
    case 501:
      throw new NotImplementedError(errorDataMessage);
    case 502:
      throw new BadGatewayError(errorDataMessage);
    case 503:
      throw new ServiceUnavailableError(errorDataMessage);
    default:
      throw new Error('An error occurred: ' + errorMessage);
  }
};
